import axios from "axios";
import { jwtDecode } from "jwt-decode";
import type { AxiosError, AxiosResponse } from "axios";
import type { IdToken } from "@auth0/auth0-react";
import type { LegacyApiResponse } from "../types/api";

type QueryValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Array<string | number | boolean>;

export type QueryParams = Record<string, QueryValue>;
export type RequestBody =
  | Record<string, unknown>
  | FormData
  | string
  | null
  | undefined;

interface SessionUser {
  token?: string;
  [key: string]: unknown;
}

interface SessionData {
  token?: string;
  user?: SessionUser;
  [key: string]: unknown;
}

interface JwtPayload {
  exp?: number;
}

interface V2ErrorResponseBody {
  success: false;
  message: string;
}

interface LegacyErrorResponseBody {
  error?: string;
  message?: string;
}

type ErrorResponseBody = V2ErrorResponseBody | LegacyErrorResponseBody;

interface AxiosHeadersDefaults {
  common?: Record<string, string>;
  delete?: Record<string, string>;
  get?: Record<string, string>;
  patch?: Record<string, string>;
  post?: Record<string, string>;
  put?: Record<string, string>;
}

interface SafeAxiosDefaults {
  baseURL?: string;
  headers?: AxiosHeadersDefaults;
}

const toSearchParamsRecord = (params: QueryParams): Record<string, string> => {
  const entries = Object.entries(params).flatMap(([key, value]) => {
    if (value === null || value === undefined) {
      return [];
    }

    if (Array.isArray(value)) {
      return [[key, value.join(",")]];
    }

    return [[key, String(value)]];
  });

  return Object.fromEntries(entries);
};

const AUTH_SESSION_KEY = "ubold_user";

const getSafeAxiosDefaults = (): SafeAxiosDefaults => {
  const axiosWithDefaults = axios as typeof axios & {
    defaults?: SafeAxiosDefaults;
  };

  if (!axiosWithDefaults.defaults) {
    axiosWithDefaults.defaults = {
      headers: {},
    } as typeof axios.defaults & SafeAxiosDefaults;
  }

  const defaults = axiosWithDefaults.defaults;
  const safeHeaders = (defaults.headers ??
    {}) as typeof axios.defaults.headers & AxiosHeadersDefaults;

  defaults.headers = safeHeaders;
  defaults.headers.common = defaults.headers.common ?? {};
  defaults.headers.post = defaults.headers.post ?? {};

  return defaults;
};

// Use API_URL for backend API requests, not BASE_URL (which is for frontend redirects)
const apiURL = process.env.REACT_APP_API_URL;
// Set default content type and base URL for axios
const axiosDefaults = getSafeAxiosDefaults();
axiosDefaults.headers.post["Content-Type"] = "application/json";
axiosDefaults.baseURL = apiURL;
//const testUser = 'auth0|66d95413e1ac983f011fb379'
//axios.defaults.baseURL = "https://80eight.zilo-aws.co.za/"
// Response interceptor - handles both v2 and legacy response/error formats
axios.interceptors?.response?.use?.(
  (response: AxiosResponse) => {
    // Check for v2 error responses that arrive with a 2xx status
    const body = response.data as Record<string, unknown> | undefined;
    if (body && "success" in body && body.success === false) {
      const errorMessage =
        (typeof body.message === "string" ? body.message : "") ||
        "Request failed";
      return Promise.reject(new Error(errorMessage));
    }
    return response;
  },
  (error: AxiosError<ErrorResponseBody>) => {
    if (!error.response) {
      return Promise.reject(error);
    }

    const { status, data } = error.response;

    // Handle specific HTTP status codes
    switch (status) {
      case 401:
        return Promise.reject(new Error("Invalid credentials"));
      case 403:
        window.location.href = "/unauthorized";
        return Promise.reject(new Error("Access Forbidden"));
      case 404:
        return Promise.reject(
          new Error("Sorry! the data you are looking for could not be found")
        );
      default:
        break;
    }

    // Extract error message from v2 format { success: false, message: "..." }
    // or legacy format { error: "...", message: "..." }
    let errorMessage: string;
    if (data && "success" in data && data.success === false) {
      errorMessage = (data as V2ErrorResponseBody).message;
    } else if (data?.message) {
      errorMessage = data.message;
    } else if (
      data &&
      "error" in data &&
      (data as LegacyErrorResponseBody).error
    ) {
      errorMessage = (data as LegacyErrorResponseBody).error as string;
    } else {
      errorMessage = error.message || "An unknown error occurred";
    }

    return Promise.reject(new Error(errorMessage));
  }
);

// Function to set the default authorization header
const setAuthorization = (token?: string) => {
  const defaults = getSafeAxiosDefaults();

  if (token) {
    defaults.headers.common["Authorization"] = "Bearer " + token;
  } else {
    delete defaults.headers.common["Authorization"];
  }
};

// Function to get user from session storage
const getUserFromSession = (): SessionData | null => {
  const user = sessionStorage.getItem(AUTH_SESSION_KEY);
  return user ? (JSON.parse(user) as SessionData) : null;
};

//function to get profile

// Fetch data with GET method
const get = <TResponse = LegacyApiResponse>(
  url: string,
  params?: QueryParams
) => {
  const queryString = params
    ? "?" + new URLSearchParams(toSearchParamsRecord(params)).toString()
    : "";
  return axios.get<TResponse>(url + queryString);
};

const getQuery = <TResponse = LegacyApiResponse>(
  url: string,
  params?: QueryParams,
  config: Omit<Parameters<typeof axios.get<TResponse>>[1], "params"> = {}
) => {
  return axios.get<TResponse>(url, { ...config, params });
};

// Fetch a file
const getFile = (url: string, params?: QueryParams) => {
  const queryString = params
    ? "?" + new URLSearchParams(toSearchParamsRecord(params)).toString()
    : "";
  return axios.get(url + queryString, { responseType: "blob" });
};

// Create a new resource with POST method
const create = <TResponse = LegacyApiResponse>(
  url: string,
  data?: RequestBody
) => {
  return axios.post<TResponse>(url, data);
};

// Update data with PATCH method
const updatePatch = <TResponse = LegacyApiResponse>(
  url: string,
  params: QueryParams = {},
  data: RequestBody = {},
  config: Parameters<typeof axios.patch<TResponse>>[2] = {}
) => {
  return axios.patch<TResponse>(url, data, {
    ...config,
    params, // Add query parameters here
  });
};

// Update data with PUT method
const update = (
  url: string,
  params: QueryParams = {},
  data: RequestBody = {},
  config: Parameters<typeof axios.put>[2] = {}
) => {
  //return axios.put(url, data);
  return axios.put(url, data, {
    ...config,
    params, // Add query parameters here
  });
};

// Delete a resource
const deleteResource = (
  url: string,
  params: QueryParams = {},
  data: RequestBody = {},
  config: Parameters<typeof axios.delete>[1] = {}
) => {
  //return axios.delete(url);
  return axios.delete(url, {
    ...config,
    params, // Query parameters should be included here
    data, // Body data (in case of DELETE, data might be required)
  });
};

// Create a resource with file upload
/*const createWithFile = (url, data) => {
  const formData = new FormData();
  /*for (const key in data) {
    formData.append(key, data[key]);
  }
  formData.append("data", JSON.stringify(data));

  return axios.post(url, formData, {
    headers: {
      ...axios.defaults.headers,
      "Content-Type": "multipart/form-data",
    },
  });
};*/
// const createWithFile = (url, data, files = []) => {
//   const formData = new FormData();
//   console.log("Files: ", files);

//   // Add the main data as a JSON string
//   formData.append("data", JSON.stringify(data));

//   // Add files to FormData
//   /*files.forEach((file, index) => {
//     formData.append(`files[${index}]`, file);
//   });*/
//   files.forEach((file) => {
//     formData.append("images", file /*.originFileObj*/); // Ensure original file object is appended
//   });

//   console.log(formData);

//   return axios.post(url, formData, {
//     headers: {
//       "Content-Type": "multipart/form-data",
//     },
//   });
// };

// const createWithFile = (url, data, files = []) => {
//   console.log("createWithFile", data);
//   const formData = new FormData();

//   files.forEach((file) => {
//     formData.append("images", file);
//   });

//   return axios.post(url, formData, {
//     headers: {
//       "Content-Type": "multipart/form-data",
//     },
//   });
// };

const createWithFile = (
  url: string,
  data?: RequestBody,
  files: File[] = []
) => {
  const formData = new FormData();

  const appendFormData = (
    fd: FormData,
    formValue: RequestBody,
    parentKey = ""
  ): void => {
    if (formValue instanceof FormData) {
      formValue.forEach((value, key) => {
        fd.append(key, value);
      });
      return;
    }

    if (
      formValue &&
      typeof formValue === "object" &&
      !(formValue instanceof File) &&
      !Array.isArray(formValue)
    ) {
      Object.entries(formValue).forEach(([key, value]) => {
        const formKey = parentKey ? `${parentKey}[${key}]` : key;
        appendFormData(fd, value as RequestBody, formKey);
      });
      return;
    }

    if (Array.isArray(formValue)) {
      formValue.forEach((value, index) => {
        const formKey = parentKey ? `${parentKey}[${index}]` : String(index);
        appendFormData(fd, value as RequestBody, formKey);
      });
      return;
    }

    if (formValue instanceof File) {
      fd.append(parentKey, formValue);
      return;
    }

    if (parentKey) {
      fd.append(parentKey, String(formValue ?? ""));
    }
  };

  appendFormData(formData, data);

  files.forEach((file) => {
    formData.append("images", file);
  });

  return axios.post(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

const updateWithFile = (url: string, data: RequestBody, files: File[] = []) => {
  const formData = new FormData();

  if (data instanceof FormData) {
    data.forEach((value, key) => {
      formData.append(key, value);
    });
  } else if (data && typeof data === "object") {
    Object.entries(data).forEach(([key, value]) => {
      formData.append(key, String(value ?? ""));
    });
  } else if (typeof data === "string") {
    formData.append("value", data);
  }

  files.forEach((file) => {
    formData.append("images", file);
  });

  return axios.patch(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// // Update with file upload
// const updateWithFile = (url, data) => {
//   const formData = new FormData();
//   for (const key in data) {
//     formData.append(key, data[key]);
//   }

//   return axios.patch(url, formData, {
//     headers: {
//       ...axios.defaults.headers,
//       "Content-Type": "multipart/form-data",
//     },
//   });
// };

// Add images to a resource with file upload
const addImages = (url: string, images: File[]) => {
  if (!Array.isArray(images) || images.length === 0) {
    throw new Error("images must be a non-empty array of files.");
  }

  const formData = new FormData();
  images.forEach((image, _index) => {
    formData.append("images", image); // Use dynamic keys for multiple images
  });

  return axios.post(url, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

// Check if the user is authenticated
const isUserAuthenticated = () => {
  const user = getLoggedInUser();
  if (!user) {
    return false;
  }

  const decoded = jwtDecode<JwtPayload>(user.token ?? "");
  const currentTime = Date.now() / 1000;
  if (!decoded.exp || decoded.exp < currentTime) {
    return false;
  }
  return true;
};

// Set logged-in user
const setLoggedInUser = (session?: SessionData | IdToken) => {
  if (session) {
    sessionStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session));
  } else {
    sessionStorage.removeItem(AUTH_SESSION_KEY);
  }
};

// Get logged-in user
const getLoggedInUser = () => {
  return getUserFromSession();
};

// Set user in session
const setUserInSession = (modifiedUser: Record<string, unknown>) => {
  let userInfo = sessionStorage.getItem(AUTH_SESSION_KEY);
  if (userInfo) {
    const { token, user } = JSON.parse(userInfo) as SessionData;
    setLoggedInUser({ token, user: { ...user, ...modifiedUser } });
  }
};

// Check if token is available in session
const user = getUserFromSession();
if (user) {
  const { token } = user;
  if (token) {
    setAuthorization(token);
  }
}

export const axiosInstance = axios;

export {
  create,
  createWithFile,
  deleteResource,
  get,
  getFile,
  getLoggedInUser,
  getQuery,
  isUserAuthenticated,
  setAuthorization,
  setLoggedInUser,
  setUserInSession,
  update,
  updatePatch,
  updateWithFile,
  addImages,
};
